import { useEffect, useMemo, useRef, useState } from 'react';
import {
  audioSources,
  effectUrl,
  formatBytes,
  frameRect,
  imageUrl,
  loadManifest,
  walkFrames,
  type ImageEntry,
  type Manifest,
} from '../assets/manifest.js';
import { createEffectPlayer, type EffectPlayer } from '../assets/effekseer.js';
import { useAuth } from '../auth/AuthContext.js';

const DIRECTIONS = ['baixo', 'esquerda', 'direita', 'cima'] as const;

/** Desenha um quadro da folha, ampliado em número inteiro para não borrar. */
function SheetFrame({
  url,
  entry,
  frame,
  scale,
}: {
  url: string;
  entry: ImageEntry;
  frame: number;
  scale: number;
}): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const image = new Image();
    image.onload = () => {
      const rect = entry.grid
        ? frameRect(entry.grid, frame)
        : { x: 0, y: 0, width: entry.width, height: entry.height };

      canvas.width = rect.width * scale;
      canvas.height = rect.height * scale;
      // Arte de pixel só sobrevive à ampliação com vizinho mais próximo.
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(
        image, rect.x, rect.y, rect.width, rect.height,
        0, 0, canvas.width, canvas.height,
      );
    };
    image.src = url;
  }, [url, entry, frame, scale]);

  return <canvas ref={canvasRef} className="frame" />;
}

function SpritePanel({ manifest }: { manifest: Manifest }): JSX.Element {
  const groups = Object.keys(manifest.images);
  const [group, setGroup] = useState('characters');
  const sheets = useMemo(() => Object.keys(manifest.images[group] ?? {}), [manifest, group]);
  const [sheet, setSheet] = useState('distortidus');
  const [character, setCharacter] = useState(0);
  const [direction, setDirection] = useState(0);
  const [step, setStep] = useState(0);

  const entry = manifest.images[group]?.[sheet];

  useEffect(() => {
    if (!sheets.includes(sheet)) setSheet(sheets[0] ?? '');
  }, [sheets, sheet]);

  // Vaivém da caminhada: quatro passos por ciclo, a cinco quadros por segundo.
  useEffect(() => {
    if (!entry?.grid?.framesPerCharacter) return;
    const id = window.setInterval(() => setStep((s) => (s + 1) % 4), 200);
    return () => window.clearInterval(id);
  }, [entry]);

  if (!entry) return <p className="muted">nenhuma folha neste grupo</p>;

  const url = imageUrl(manifest, group, sheet);
  const isCharacter = Boolean(entry.grid?.framesPerCharacter);
  const perSheet = entry.grid?.charactersPerSheet ?? 1;
  const frames = isCharacter && entry.grid ? walkFrames(entry.grid, character, direction) : [0];
  const frame = frames[step % frames.length] ?? 0;

  return (
    <>
      <div className="controls">
        <label>
          grupo
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g} ({Object.keys(manifest.images[g] ?? {}).length})
              </option>
            ))}
          </select>
        </label>
        <label>
          folha
          <select value={sheet} onChange={(e) => setSheet(e.target.value)}>
            {sheets.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </label>
        {isCharacter && perSheet > 1 && (
          <label>
            personagem
            <select value={character} onChange={(e) => setCharacter(Number(e.target.value))}>
              {Array.from({ length: perSheet }, (_, i) => (
                <option key={i} value={i}>{i + 1}</option>
              ))}
            </select>
          </label>
        )}
        {isCharacter && (
          <label>
            direção
            <select value={direction} onChange={(e) => setDirection(Number(e.target.value))}>
              {DIRECTIONS.map((d, i) => (
                <option key={d} value={i}>{d}</option>
              ))}
            </select>
          </label>
        )}
      </div>

      <div className="stage">
        <SheetFrame url={url} entry={entry} frame={frame} scale={isCharacter ? 3 : 1} />
      </div>

      <dl className="facts">
        <div><dt>arquivo</dt><dd className="mono">{entry.file}</dd></div>
        <div><dt>dimensões</dt><dd className="mono">{entry.width}×{entry.height}</dd></div>
        {entry.grid && (
          <div>
            <dt>quadro</dt>
            <dd className="mono">{entry.grid.frameWidth}×{entry.grid.frameHeight} · grade {entry.grid.columns}×{entry.grid.rows}</dd>
          </div>
        )}
        <div>
          <dt>tamanho</dt>
          <dd className="mono">
            {formatBytes(entry.bytes)}
            {entry.bytes < entry.sourceBytes && (
              <span className="good"> ({Math.round((1 - entry.bytes / entry.sourceBytes) * 100)}% menor)</span>
            )}
          </dd>
        </div>
      </dl>
    </>
  );
}

function AudioPanel({ manifest }: { manifest: Manifest }): JSX.Element {
  const groups = Object.keys(manifest.audio);
  const [group, setGroup] = useState('bgm');
  const tracks = useMemo(() => Object.keys(manifest.audio[group] ?? {}), [manifest, group]);
  const [track, setTrack] = useState('Battle1');
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (!tracks.includes(track)) setTrack(tracks[0] ?? '');
  }, [tracks, track]);

  useEffect(() => {
    audioRef.current?.load();
  }, [track, group]);

  const entry = manifest.audio[group]?.[track];
  if (!entry) return <p className="muted">nenhum som neste grupo</p>;

  const sources = audioSources(manifest, group, track);

  return (
    <>
      <div className="controls">
        <label>
          grupo
          <select value={group} onChange={(e) => setGroup(e.target.value)}>
            {groups.map((g) => (
              <option key={g} value={g}>
                {g} ({Object.keys(manifest.audio[g] ?? {}).length})
              </option>
            ))}
          </select>
        </label>
        <label>
          faixa
          <select value={track} onChange={(e) => setTrack(e.target.value)}>
            {tracks.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </label>
      </div>

      <audio ref={audioRef} controls preload="none" className="player">
        {sources.map((s) => (
          <source key={s.src} src={s.src} type={s.type} />
        ))}
      </audio>

      <dl className="facts">
        <div><dt>formatos</dt><dd className="mono">{sources.map((s) => s.type.split('/')[1]).join(' · ')}</dd></div>
        <div><dt>papel</dt><dd>{entry.role}</dd></div>
        <div><dt>tamanho</dt><dd className="mono">{formatBytes(entry.bytes)}</dd></div>
      </dl>
    </>
  );
}

function EffectPanel({ manifest }: { manifest: Manifest }): JSX.Element {
  const effects = useMemo(() => Object.keys(manifest.fx.effects ?? {}), [manifest]);
  const [effect, setEffect] = useState('SlashPhysical');
  const [state, setState] = useState<'inicial' | 'carregando' | 'pronto' | 'erro'>('inicial');
  const [message, setMessage] = useState('');
  const [peak, setPeak] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const playerRef = useRef<EffectPlayer | null>(null);

  useEffect(() => () => playerRef.current?.dispose(), []);

  const play = async (): Promise<void> => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    setState('carregando');
    setMessage('');
    try {
      playerRef.current ??= await createEffectPlayer(canvas, setPeak);
      setPeak(0);
      await playerRef.current.play(effectUrl(manifest, effect));
      setState('pronto');
    } catch (e) {
      setState('erro');
      setMessage(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <>
      <div className="controls">
        <label>
          efeito
          <select value={effect} onChange={(e) => setEffect(e.target.value)}>
            {effects.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => void play()} disabled={state === 'carregando'}>
          {state === 'carregando' ? 'carregando…' : 'tocar'}
        </button>
      </div>

      <div className="stage">
        <canvas ref={canvasRef} width={320} height={320} className="fxcanvas" />
      </div>

      {state === 'erro' && <p className="bad">{message}</p>}
      <dl className="facts">
        <div><dt>efeitos no acervo</dt><dd className="mono">{effects.length}</dd></div>
        <div><dt>runtime</dt><dd className="mono">Effekseer (WebAssembly)</dd></div>
        <div>
          <dt>pixels no pico</dt>
          <dd className="mono" data-peak={peak}>
            {peak > 0 ? <span className="good">{peak.toLocaleString('pt-BR')}</span> : '—'}
          </dd>
        </div>
      </dl>
    </>
  );
}

export function AssetBrowser(): JSX.Element {
  const { user } = useAuth();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user === null) return;
    loadManifest()
      .then(setManifest)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [user]);

  if (user === null) {
    return (
      <div className="auth-locked-view">
        <div className="admin-locked">
          <span className="admin-locked-icon">🎨</span>
          <h2>Acervo Bloqueado</h2>
          <p>Você precisa estar conectado a uma conta para navegar e visualizar o acervo.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <section>
        <h2>Acervo</h2>
        <p className="bad">Manifesto não carregou: {error}</p>
        <p className="muted">Rode <code>make assets</code> para gerar o acervo.</p>
      </section>
    );
  }

  if (!manifest) return <section><h2>Acervo</h2><p className="muted">carregando…</p></section>;

  const imageCount = Object.values(manifest.images).reduce((n, g) => n + Object.keys(g).length, 0);
  const audioCount = Object.values(manifest.audio).reduce((n, g) => n + Object.keys(g).length, 0);
  const contentCount = Object.values(manifest.content).reduce((n, c) => n + c.count, 0);

  return (
    <>
      <section>
        <h2>Acervo</h2>
        <dl className="facts wide">
          <div><dt>imagens</dt><dd className="mono">{imageCount}</dd></div>
          <div><dt>sons</dt><dd className="mono">{audioCount}</dd></div>
          <div><dt>animações</dt><dd className="mono">{Object.keys(manifest.fx.effects ?? {}).length}</dd></div>
          <div><dt>registros de conteúdo</dt><dd className="mono">{contentCount}</dd></div>
          <div><dt>excluídos</dt><dd className="mono">{manifest.excluded.length}</dd></div>
        </dl>
      </section>

      <section data-panel="sprites"><h2>Sprites</h2><SpritePanel manifest={manifest} /></section>
      <section data-panel="audio"><h2>Áudio</h2><AudioPanel manifest={manifest} /></section>
      <section data-panel="fx"><h2>Animações</h2><EffectPanel manifest={manifest} /></section>
    </>
  );
}
